import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation } from "wouter";
import { Loader2, Phone, KeyRound, ArrowRight, ArrowLeft, User, Sparkles } from "lucide-react";
import { z } from "zod";
import { useLogin, useVerifyOtp, useResendOtp, useUpdateProfile, useIsAuthenticated } from "@/hooks/use-auth";
import { useWalletBalance } from "@/hooks/use-wallet";
import { useToast } from "@/hooks/use-toast";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn, normalizeKuwaitPhoneInput } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const phoneSchema = z.object({
  // Exactly 8 local digits — the +965 prefix is added on submit. Anything
  // longer (autofilled country codes) is stripped by normalizeKuwaitPhoneInput
  // before it reaches validation.
  phone: z.string().regex(/^\d{8}$/, "Enter your 8-digit phone number"),
});

const KUWAIT_PREFIX = "+965";

export default function Login() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const isAuthenticated = useIsAuthenticated();

  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [userName, setUserName] = useState('');
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const { data: walletData } = useWalletBalance();

  const queryParams = new URLSearchParams(window.location.search);
  const rawRedirect = queryParams.get("redirect");
  const redirectUrl = rawRedirect && rawRedirect.startsWith("/") && !rawRedirect.includes("://") && !rawRedirect.startsWith("//")
    ? rawRedirect
    : "/booking";

  const loginMutation = useLogin();
  const verifyMutation = useVerifyOtp();
  const resendMutation = useResendOtp();
  const updateProfileMutation = useUpdateProfile();

  // Redirect if already authenticated and has name
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (isAuthenticated && user.name) {
          setLocation("/");
        }
      } catch {
        if (isAuthenticated) {
          setLocation("/");
        }
      }
    }
  }, [isAuthenticated, setLocation]);

  const phoneForm = useForm<z.infer<typeof phoneSchema>>({
    resolver: zodResolver(phoneSchema),
    defaultValues: { phone: "" },
  });


  const startResendCooldown = () => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const onPhoneSubmit = async (data: z.infer<typeof phoneSchema>) => {
    try {
      const fullPhone = `+965${data.phone}`;
      await loginMutation.mutateAsync(fullPhone);
      setPhoneNumber(fullPhone);
      setStep('otp');
      startResendCooldown();
      toast({
        title: t("login.otpSent"),
        description: t("login.checkWhatsApp"),
      });
    } catch (error: any) {
      toast({
        title: t("login.error"),
        description: error.message || t("login.failedToSendOtp"),
        variant: "destructive",
      });
    }
  };

  const onOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast({
        title: t("login.error"),
        description: t("login.enterSixDigits"),
        variant: "destructive",
      });
      return;
    }
    try {
      const result = await verifyMutation.mutateAsync({ phone: phoneNumber, otpCode });
      toast({
        title: t("login.success"),
        description: t("login.welcomeBack"),
      });
      if (!result.user.name || result.user.name.trim() === '') {
        setShowNameDialog(true);
      } else {
        // Use window.location.replace to avoid back button returning to login
        window.location.replace(redirectUrl);
      }
    } catch (error: any) {
      toast({
        title: t("login.error"),
        description: error.message || t("login.invalidOtp"),
        variant: "destructive",
      });
    }
  };

  const handleSaveName = async () => {
    if (!userName.trim()) {
      toast({
        title: t("login.error"),
        description: t("booking.validation.nameRequired"),
        variant: "destructive",
      });
      return;
    }
    try {
      await updateProfileMutation.mutateAsync({ name: userName.trim() });
      setShowNameDialog(false);
      // Show welcome dialog for new users
      setShowWelcomeDialog(true);
    } catch (error: any) {
      toast({
        title: t("login.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await resendMutation.mutateAsync(phoneNumber);
      startResendCooldown();
      toast({
        title: t("login.otpResent"),
        description: t("login.checkWhatsApp"),
      });
    } catch (error: any) {
      toast({
        title: t("login.error"),
        description: error.message || t("login.failedToResendOtp"),
        variant: "destructive",
      });
    }
  };

  const handleBack = () => {
    setStep('phone');
    setOtpCode('');
  };

  return (
    <div className="min-h-screen pt-24 pb-24 flex items-center justify-center bg-background">
      <div className="container mx-auto px-4 max-w-md">
        <div className="bg-card rounded-3xl p-8 shadow-xl shadow-blue-900/5 border border-border/50">
          {step === 'phone' ? (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-bold font-display mb-2">{t("login.title")}</h1>
                <p className="text-muted-foreground">{t("login.subtitle")}</p>
              </div>

              <Form {...phoneForm}>
                <form onSubmit={phoneForm.handleSubmit(onPhoneSubmit)} className="space-y-6">
                  <FormField
                    control={phoneForm.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("login.phoneNumber")}</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                              +965
                            </span>
                            <Input
                              type="tel"
                              placeholder={t("login.phonePlaceholder")}
                              maxLength={8}
                              inputMode="numeric"
                              pattern="[0-9]*"
                              {...field}
                              onChange={(e) => field.onChange(normalizeKuwaitPhoneInput(e.target.value))}
                              className={cn("rounded-xl h-12 pl-14", isRtl && "text-right")}
                              dir="ltr"
                              data-testid="input-phone"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full h-12 rounded-xl text-lg font-semibold"
                    disabled={loginMutation.isPending}
                    data-testid="button-send-otp"
                  >
                    {loginMutation.isPending ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        {t("login.sendOtp")}
                        {isRtl ? <ArrowLeft className="ms-2 h-5 w-5" /> : <ArrowRight className="ms-2 h-5 w-5" />}
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-bold font-display mb-2">{t("login.verifyTitle")}</h1>
                <p className="text-muted-foreground">
                  {t("login.otpSentTo")} <span className="font-medium text-foreground" dir="ltr">{phoneNumber}</span>
                </p>
              </div>

              <form onSubmit={onOtpSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none" htmlFor="otp-input">
                    {t("login.enterOtp")}
                  </label>
                  <input
                    id="otp-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    className="flex w-full rounded-xl border border-input bg-background px-3 py-4 text-center text-2xl tracking-widest font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    dir="ltr"
                    data-testid="input-otp"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 rounded-xl text-lg font-semibold"
                  disabled={verifyMutation.isPending}
                  data-testid="button-verify-otp"
                >
                  {verifyMutation.isPending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    t("login.verify")
                  )}
                </Button>

                <div className="flex items-center justify-between gap-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleBack}
                    className="flex-1"
                    data-testid="button-back"
                  >
                    {isRtl ? <ArrowRight className="me-2 h-4 w-4" /> : <ArrowLeft className="me-2 h-4 w-4" />}
                    {t("login.back")}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleResend}
                    disabled={resendCooldown > 0 || resendMutation.isPending}
                    className="flex-1"
                    data-testid="button-resend"
                  >
                    {resendMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : resendCooldown > 0 ? (
                      `${t("login.resendIn")} ${resendCooldown}s`
                    ) : (
                      t("login.resend")
                    )}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      <Dialog open={showNameDialog} onOpenChange={setShowNameDialog}>
        <DialogContent className="sm:max-w-md" dir={isRtl ? "rtl" : "ltr"}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              {t("login.nameRequired")}
            </DialogTitle>
            <DialogDescription>
              {t("login.nameRequiredDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="userName" className="text-sm font-medium">
                {t("login.enterName")}
              </label>
              <Input
                id="userName"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder={t("login.namePlaceholder")}
                className="h-12 rounded-xl"
                dir={isRtl ? "rtl" : "ltr"}
                data-testid="input-user-name"
              />
            </div>
            <Button
              onClick={handleSaveName}
              className="w-full h-12 rounded-xl"
              disabled={updateProfileMutation.isPending}
              data-testid="button-save-name"
            >
              {updateProfileMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                t("login.saveAndContinue")
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Welcome Dialog */}
      <Dialog open={showWelcomeDialog} onOpenChange={(open) => { if (!open) window.location.replace(redirectUrl); }}>
        <DialogContent className="sm:max-w-md text-center">
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <DialogHeader className="items-center text-center">
              <DialogTitle className="text-xl font-bold">{t("welcome.title")}</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-2">
                {(walletData?.balance ?? 0) > 0
                  ? t("welcome.withBalance", { balance: (walletData?.balance ?? 0).toFixed(3), currency: t("services.currency") })
                  : t("welcome.description")}
              </DialogDescription>
            </DialogHeader>
            <Button className="w-full rounded-xl" onClick={() => window.location.replace(redirectUrl)}>
              {t("welcome.startBooking")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
